import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { HelpCircle, X, Globe, Mail, Landmark, Briefcase, CreditCard, Inbox, Sparkles, Search, Check, PartyPopper } from "lucide-react";

const SOURCES = [
  {
    id: "espace-client",
    icon: Globe,
    title: "Espace client de l'assureur",
    text: "Connectez-vous au site ou à l'appli de chaque assureur, rubrique « Mes contrats » ou « Mes documents ». Téléchargez l'attestation annuelle et les conditions particulières (PDF).",
  },
  {
    id: "boite-mail",
    icon: Mail,
    title: "Votre boîte mail",
    text: "Recherchez : « attestation », « avenant », « cotisation », « échéancier », « assurance », ou le nom d'un assureur. Pensez à ouvrir les pièces jointes PDF.",
  },
  {
    id: "releves-bancaires",
    icon: Landmark,
    title: "Vos relevés bancaires",
    text: "Repérez les prélèvements récurrents (libellés « PRLV … ASSURANCE », mutuelle, MAIF, MACIF…). C'est le meilleur moyen de retrouver un contrat oublié.",
  },
  {
    id: "employeur",
    icon: Briefcase,
    title: "Via votre employeur",
    text: "Mutuelle santé collective et prévoyance : demandez la notice d'information au service RH. Ces contrats ne sont pas toujours dans vos mails personnels.",
  },
  {
    id: "cartes-bancaires",
    icon: CreditCard,
    title: "Vos cartes bancaires",
    text: "Visa Premier, Gold Mastercard, American Express… incluent des assurances (voyage, location de voiture, achats). Consultez le contrat/notice de votre carte.",
  },
  {
    id: "papier",
    icon: Inbox,
    title: "Courrier & classeur papier",
    text: "Contrats signés et conditions générales reçus par la poste. Numérisez-les (photo ou scan en PDF) pour les importer ici.",
  },
  {
    id: "a-ne-pas-oublier",
    icon: Sparkles,
    title: "À ne pas oublier",
    text: "Assurance emprunteur (prêt immobilier), assurance scolaire des enfants, assurance téléphone de l'opérateur, extensions de garantie & garanties constructeur.",
  },
];

export const HelpGuide = ({ variant = "button", onboarding = false, controlledOpen = null, onClose, onAddCards }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== null;
  const open = isControlled ? controlledOpen : internalOpen;

  const [checked, setChecked] = useState([]);
  const total = SOURCES.length;
  const done = checked.length;
  const allDone = done === total;

  const loadChecklist = useCallback(async () => {
    try {
      const { data } = await api.get("/checklist");
      setChecked(Array.isArray(data.checked) ? data.checked : []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (open) loadChecklist();
  }, [open, loadChecklist]);

  const toggle = async (id) => {
    const next = checked.includes(id) ? checked.filter((c) => c !== id) : [...checked, id];
    setChecked(next);
    try {
      await api.put("/checklist", { checked: next });
    } catch {
      /* best-effort */
    }
  };

  const close = () => {
    if (isControlled) onClose?.();
    else setInternalOpen(false);
  };

  return (
    <>
      {variant === "icon" && !isControlled && (
        <button
          data-testid="help-guide-icon"
          onClick={() => setInternalOpen(true)}
          title="Où trouver mes contrats ?"
          className="w-9 h-9 grid place-items-center rounded-xl border border-border hover:bg-muted transition"
        >
          <HelpCircle className="w-4 h-4" />
        </button>
      )}
      {variant === "button" && !isControlled && (
        <button
          data-testid="help-guide-btn"
          onClick={() => setInternalOpen(true)}
          className="flex items-center gap-2 text-sm font-medium px-4 h-10 rounded-xl border border-border hover:bg-muted transition"
        >
          <HelpCircle className="w-4 h-4" /> Où trouver mes contrats ?
        </button>
      )}

      {open && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm" onClick={close} data-testid="help-modal">
          <div className="w-full max-w-lg max-h-[88vh] flex flex-col rounded-2xl border border-border bg-card shadow-xl animate-in-up" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center">
                    {onboarding ? <PartyPopper className="w-4 h-4" /> : <Search className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className="font-heading font-bold leading-none">
                      {onboarding ? "Bienvenue sur Iacover 👋" : "Où trouver vos contrats d'assurance ?"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {onboarding ? "Rassemblez d'abord vos contrats, on s'occupe du reste" : "Cochez au fur et à mesure ce que vous avez rassemblé"}
                    </p>
                  </div>
                </div>
                <button onClick={close} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-muted transition shrink-0" data-testid="help-close">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Progression */}
              <div className="mt-4" data-testid="checklist-progress">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-mono uppercase tracking-wide text-muted-foreground">Progression</span>
                  <span className="font-semibold">{done}/{total} vérifié(s)</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${allDone ? "bg-emerald-500" : "bg-primary"}`}
                    style={{ width: `${(done / total) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-2.5">
              {SOURCES.map((s) => {
                const isChecked = checked.includes(s.id);
                return (
                  <button
                    key={s.id}
                    data-testid={`checklist-item-${s.id}`}
                    onClick={() => toggle(s.id)}
                    className={`w-full text-left flex gap-3.5 p-3 rounded-xl border transition ${
                      isChecked ? "border-emerald-500/40 bg-emerald-500/5" : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <div
                      className={`w-6 h-6 rounded-md grid place-items-center shrink-0 mt-0.5 border transition ${
                        isChecked ? "bg-emerald-500 border-emerald-500 text-white" : "border-input bg-card text-transparent"
                      }`}
                    >
                      <Check className="w-4 h-4" strokeWidth={3} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm flex items-center gap-2 ${isChecked ? "line-through text-muted-foreground" : ""}`}>
                        <s.icon className="w-4 h-4 text-primary shrink-0" />
                        {s.title}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">{s.text}</p>
                    </div>
                  </button>
                );
              })}

              {allDone ? (
                <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4 flex items-start gap-3">
                  <PartyPopper className="w-5 h-5 shrink-0 text-emerald-600 mt-0.5" />
                  <p className="text-sm text-foreground">
                    <span className="font-semibold">Bravo, tout est coché !</span> Déposez vos PDF dans « Mes Contrats » ou lancez la « Recherche auto » pour l'analyse.
                  </p>
                </div>
              ) : (
                <div className="rounded-xl bg-accent border border-ochre/25 p-4 flex items-start gap-3">
                  <Sparkles className="w-5 h-5 shrink-0 text-ochre mt-0.5" />
                  <p className="text-sm text-accent-foreground">
                    <span className="font-semibold">Astuce :</span> l'onglet <span className="font-semibold">« Recherche auto »</span> scanne vos emails et prélèvements pour retrouver vos contrats automatiquement.
                  </p>
                </div>
              )}
            </div>

            {onboarding && (
              <div className="p-4 border-t border-border flex flex-wrap items-center justify-between gap-2">
                <button onClick={close} data-testid="onboarding-skip" className="text-sm font-medium text-muted-foreground hover:text-foreground transition px-3 h-10">
                  Passer le guide de démarrage
                </button>
                <div className="flex items-center gap-2">
                  {onAddCards && (
                    <button onClick={onAddCards} data-testid="onboarding-add-cards" className="flex items-center gap-2 h-10 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted transition">
                      <CreditCard className="w-4 h-4" /> Mes cartes bancaires
                    </button>
                  )}
                  <button onClick={close} data-testid="onboarding-start" className="flex items-center gap-2 h-10 px-5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition">
                    C'est parti
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default HelpGuide;
